import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { PROVINCES, USER_ROLES } from "@/lib/constants";
import { ListingCard } from "@/components/ListingCard";
import { VerificationBadges } from "@/components/VerificationBadges";
import { BRAND, CiftoraLogo } from "@/lib/brand";
import { useSeo } from "@/lib/seo";
import { track, trackEvent } from "@/lib/analytics";

const shell = "mx-auto w-full max-w-md px-4 py-12";
const input =
  "h-12 w-full rounded-lg border border-input bg-card px-3 text-base outline-none focus:border-secondary";

const Wrap = ({ title, subtitle, children, testid }) => (
  <div className={shell} data-testid={testid}>
    <Link to="/" className="mb-8 flex flex-col items-start gap-2">
      <CiftoraLogo variant="primary" className="h-14" testid="auth-logo" />
      <span className="text-sm font-semibold tracking-wide text-secondary">{BRAND.slogan}</span>
    </Link>
    <h1 className="font-display text-3xl font-extrabold text-primary">{title}</h1>
    {subtitle && <p className="mt-2 text-base text-muted-foreground">{subtitle}</p>}
    <div className="mt-8">{children}</div>
  </div>
);

const GoogleButton = () => (
  <Button
    type="button"
    variant="outline"
    data-testid="google-login-btn"
    className="h-14 w-full rounded-lg border-2 text-base font-bold"
    onClick={() => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/panel";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }}
  >
    Google ile devam et
  </Button>
);

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const nextUrl = new URLSearchParams(window.location.search).get("next");
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await login(form.email, form.password);
      track("login");
      toast.success("Hoş geldiniz");
      if (u?.needs_profile) {
        navigate("/hesap-tamamla", { replace: true });
        return;
      }
      if (nextUrl && nextUrl.startsWith("/")) {
        navigate(nextUrl, { replace: true });
        return;
      }
      navigate(["Moderator", "Admin", "SuperAdmin", "SupportAgent"].includes(u.role) ? "/yonetim" : "/panel");
    } catch (e2) {
      setErr(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Wrap title="Giriş Yap" subtitle="Ciftora hesabınıza giriş yapın." testid="login-page">
      <form onSubmit={submit} className="space-y-4">
        <Field label="E-posta">
          <input data-testid="login-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
        </Field>
        <Field label="Şifre">
          <input data-testid="login-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} />
        </Field>
        {err && <p data-testid="login-error" className="rounded-lg bg-destructive/10 p-3 text-sm font-semibold text-destructive">{err}</p>}
        <Button type="submit" disabled={busy} data-testid="login-submit" className="h-14 w-full rounded-lg text-base font-bold">
          {busy ? "Giriş yapılıyor..." : "Giriş Yap"}
        </Button>
        <GoogleButton />
        <div className="flex justify-between pt-2 text-sm">
          <Link to="/sifremi-unuttum" data-testid="forgot-link" className="font-semibold text-secondary hover:underline">
            Şifremi unuttum
          </Link>
          <Link to="/kayit" data-testid="to-register-link" className="font-semibold text-secondary hover:underline">
            Hesap oluştur
          </Link>
        </div>
      </form>
    </Wrap>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const ref = (params.get("ref") || "").trim().toUpperCase();
  const gateSource = params.get("kaynak") || "";
  const nextPath = params.get("next") || "";

  useEffect(() => {
    trackEvent("SIGN_UP_STARTED", { source: gateSource, next: nextPath });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "", phone: "",
    province: "", district: "", role: "IndividualUser",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!ref) return;
    api.get(`/referrals/check/${ref}`).then((r) => setInvite(r.data)).catch(() => setInvite(null));
  }, [ref]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await register({ ...form, referral_code: invite?.valid ? ref : undefined });
      track("signup", { role: form.role, referred: Boolean(invite?.valid) });
      trackEvent("SIGN_UP_COMPLETED", { source: gateSource, role: form.role });
      toast.success("Hesabınız oluşturuldu. E-posta doğrulama bağlantısı gönderildi.");
      navigate(u?.needs_profile ? "/hesap-tamamla" : (nextPath || "/panel"), { replace: true });
    } catch (e2) {
      setErr(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Wrap title="Ücretsiz Üye Ol" subtitle="Ciftora Market'te ilan vermek ve teklif almak için üye olun." testid="register-page">
      {invite?.valid && (
        <div className="mb-6 rounded-lg border border-secondary/40 bg-secondary/10 p-4" data-testid="register-invite-banner">
          <p className="text-sm font-bold text-primary">
            {invite.inviter_name} sizi Ciftora'ya davet etti
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{invite.message}</p>
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Ad">
            <input data-testid="register-first-name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={input} />
          </Field>
          <Field label="Soyad">
            <input data-testid="register-last-name" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={input} />
          </Field>
        </div>
        <Field label="E-posta">
          <input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
        </Field>
        <Field label="Telefon">
          <input data-testid="register-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+90 5xx xxx xx xx" className={input} />
        </Field>
        <Field label="Şifre (en az 6 karakter)">
          <input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="İl">
            <select data-testid="register-province" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} className={input}>
              <option value="">Seçiniz</option>
              {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="İlçe">
            <input data-testid="register-district" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} className={input} />
          </Field>
        </div>
        <Field label="Kullanıcı türü">
          <select data-testid="register-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={input}>
            {USER_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </Field>
        {err && <p data-testid="register-error" className="rounded-lg bg-destructive/10 p-3 text-sm font-semibold text-destructive">{err}</p>}
        <Button type="submit" disabled={busy} data-testid="register-submit" className="h-14 w-full rounded-lg text-base font-bold">
          {busy ? "Kaydediliyor..." : "Hesap Oluştur"}
        </Button>
        <GoogleButton />
        <p className="pt-2 text-sm">
          Hesabınız var mı?{" "}
          <Link to="/giris" data-testid="to-login-link" className="font-semibold text-secondary hover:underline">
            Giriş yapın
          </Link>
        </p>
      </form>
    </Wrap>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  return (
    <Wrap title="Şifremi Unuttum" subtitle="E-posta adresinize sıfırlama bağlantısı gönderelim." testid="forgot-page">
      {sent ? (
        <p data-testid="forgot-success" className="rounded-lg border border-secondary bg-secondary/10 p-4 text-sm font-semibold text-secondary">
          Kayıtlı e-posta varsa sıfırlama bağlantısı gönderildi. Gelen kutunuzu kontrol edin.
        </p>
      ) : (
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await api.post("/auth/forgot-password", { email });
              setSent(true);
            } catch (e2) {
              toast.error(apiError(e2));
            }
          }}
        >
          <Field label="E-posta">
            <input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={input} />
          </Field>
          <Button type="submit" data-testid="forgot-submit" className="h-14 w-full rounded-lg text-base font-bold">
            Bağlantı Gönder
          </Button>
        </form>
      )}
    </Wrap>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  return (
    <Wrap title="Yeni Şifre Belirle" testid="reset-page">
      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await api.post("/auth/reset-password", { token: params.get("token"), password });
            toast.success("Şifreniz güncellendi");
            navigate("/giris");
          } catch (e2) {
            toast.error(apiError(e2));
          }
        }}
      >
        <Field label="Yeni şifre">
          <input data-testid="reset-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className={input} />
        </Field>
        <Button type="submit" data-testid="reset-submit" className="h-14 w-full rounded-lg text-base font-bold">
          Şifreyi Güncelle
        </Button>
      </form>
    </Wrap>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [state, setState] = useState("loading");
  const { refreshUser } = useAuth();
  useEffect(() => {
    api
      .post("/auth/verify-email", { token: params.get("token") })
      .then(() => {
        setState("ok");
        refreshUser();
      })
      .catch(() => setState("error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Wrap title="E-posta Doğrulama" testid="verify-email-page">
      {state === "loading" && <p className="text-muted-foreground">Doğrulanıyor...</p>}
      {state === "ok" && (
        <div data-testid="verify-success">
          <p className="rounded-lg border border-secondary bg-secondary/10 p-4 font-semibold text-secondary">
            E-posta adresiniz doğrulandı.
          </p>
          <Button asChild className="mt-6 h-14 w-full rounded-lg text-base font-bold">
            <Link to="/panel">Panelime git</Link>
          </Button>
        </div>
      )}
      {state === "error" && (
        <p data-testid="verify-error" className="rounded-lg bg-destructive/10 p-4 font-semibold text-destructive">
          Bağlantı geçersiz veya süresi dolmuş.
        </p>
      )}
    </Wrap>
  );
}

export function AuthCallback() {
  const { googleLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const sid = new URLSearchParams((location.hash || "").replace("#", "")).get("session_id");
    if (!sid || hasProcessed.current) return;
    hasProcessed.current = true;
    googleLogin(sid)
      .then((res) => {
        const next = res?.needsProfile ? "/hesap-tamamla" : "/panel";
        window.history.replaceState(null, "", next);
        navigate(next, { replace: true });
      })
      .catch(() => {
        toast.error("Google girişi başarısız, lütfen tekrar deneyin");
        window.history.replaceState(null, "", "/giris");
        navigate("/giris", { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="p-12 text-center text-muted-foreground" data-testid="auth-callback">
      Google ile giriş yapılıyor...
    </div>
  );
}

export function SellerProfile() {
  const { id } = useParams();
  const [items, setItems] = useState([]);
  const [seller, setSeller] = useState(null);
  useEffect(() => {
    api.get("/listings", { params: { owner_id: id, page_size: 24 } }).then((r) => {
      setItems(r.data.items);
      setSeller(r.data.items[0]?.seller || null);
    });
  }, [id]);
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 lg:px-8" data-testid="seller-profile-page">
      {seller && (
        <div className="rounded-lg border border-border bg-card p-6">
          <h1 className="font-display text-2xl font-extrabold text-primary sm:text-3xl">
            {seller.first_name} {seller.last_name}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{seller.province} / {seller.district}</p>
          <div className="mt-4"><VerificationBadges verifications={seller.verifications} /></div>
        </div>
      )}
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((l) => <ListingCard key={l.id} listing={l} />)}
      </div>
      {!items.length && <p className="mt-8 text-muted-foreground">Bu satıcının yayında ilanı yok.</p>}
    </div>
  );
}

export function ContentPage() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    api.get(`/contents/${slug}`).then((r) => setC(r.data)).catch(() => setMissing(true));
  }, [slug]);
  useSeo({ title: c?.title, description: (c?.body || "").slice(0, 160), noindex: missing });

  if (missing)
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center" data-testid="content-page-missing">
        <h1 className="font-display text-2xl font-extrabold text-primary">Sayfa bulunamadı</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Aradığınız içerik yayından kaldırılmış olabilir.
        </p>
        <Link
          to="/"
          data-testid="content-page-home-link"
          className="mt-6 inline-flex h-11 items-center rounded-full bg-primary px-5 text-sm font-bold text-primary-foreground"
        >
          Ana Sayfa
        </Link>
      </div>
    );

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 lg:px-8" data-testid="content-page">
      <h1 className="font-display text-3xl font-extrabold text-primary sm:text-4xl">{c?.title || "..."}</h1>
      <p className="mt-6 whitespace-pre-line text-base leading-relaxed">{c?.body}</p>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="mb-1.5 block text-sm font-semibold text-primary">{label}</label>
    {children}
  </div>
);
